import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useProfileStore } from '../../../state/profileStore';
import { FaLightbulb, FaComments } from 'react-icons/fa';
import ProfileAssistant from './ProfileAssistant';  // Add this import
import OngoingProfileAssistant from './ongoingprofileassistant';
import { useMatrixStore } from '../../../state/matrixStore';
import { useMessagingStore } from '../../../state/messagingStore';
import { useModalStore } from '../../../state/modalStore';
import WALKTHROUGHS from '../../helpcenter/walkthroughs';
import InviteBusinessModal from '../../common/InviteBusinessModal';
import WelcomeToast from '../../common/WelcomeToast';
import { getStrings } from '../../../design/designCenterLocale';


/* Outer wrapper holds positioning for the dock group. The two DockStation
   cards inside are separated by `gap` so the second (invite) card reads
   as visually distinct from the main 5-button card. */
const DockWrapper = styled.div`
  position: fixed;
  right: calc((100vw - 90vw) / 2 - 43px + var(--scrollbar-compensation, 0px));
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 16px;
  z-index: 999;
  margin-left: 3px;

  @media (max-width: 768px) {
    bottom: 20px;
    top: auto;
    right: 50%;
    transform: translateX(50%);
    flex-direction: row;
  }
`;

const DockStation = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: row;
    width: auto;
    max-width: 90vw;
  }
`;

const AssistantButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: white;
  border: 1px solid #00aef7;
  color: #00aef7;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #00aef7;
    color: white;
  }

  &.active {
    background: #00aef7;
    color: white;
    border: 3px #facc15; // Add red border when active
    box-shadow: 0 0 0 3px #facc15; // Add outer glow
  }
`;

// Gold pulse animation — injected as plain CSS so it works with inline styles
const GOLD_PULSE_CSS = `
@keyframes eh-gold-pulse {
  0%, 100% {
    box-shadow: 0 0 0 3px rgba(250, 204, 21, 0.4), 0 0 12px rgba(250, 204, 21, 0.3);
    border-color: #facc15;
    background: rgba(250, 204, 21, 0.15);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(250, 204, 21, 0.6), 0 0 24px rgba(250, 204, 21, 0.5);
    border-color: #eab308;
    background: rgba(250, 204, 21, 0.3);
  }
}`;

const goldPulseStyle = {
  animation: 'eh-gold-pulse 1.5s ease-in-out infinite',
  borderColor: '#facc15',
  color: '#eab308',
};

const DockingStation = ({ isOwner, user: testUser, auth: testAuth }) => {
  const storeProfile = useProfileStore((s) => s.profile);
  const [activeAssistant, setActiveAssistant] = useState(null);
  const profileButtonRef = useRef(null);

  // "Invite a Business You Trust" — modal logic lives in InviteBusinessModal.
  // Just track open/close here.
  const [inviteOpen, setInviteOpen] = useState(false);

  const currentUser = testUser || storeProfile;

  // ── Lean-onboarding welcome toast (one-shot) ─────────────────────────────
  // Two trigger paths, both written by ProfileAssistant.handleNameSubmission:
  //   (a) Custom DOM event 'eh:show-welcome-toast' — fires WHILE the wizard
  //       closes, so the toast appears immediately as a visible follow-up to
  //       the name submission. This is the primary path.
  //   (b) localStorage flag `eh_show_welcome_toast_u{u_id}` — checked on
  //       DockingStation mount. Fallback for the case where the user closes
  //       the wizard and reloads the page before the toast renders. Without
  //       this fallback, the toast would silently lose its chance to fire.
  // Whichever path fires first clears both (event handler also wipes the
  // localStorage key, mount-effect also stops listening once it sets state)
  // so the toast is strictly one-shot per signup.
  const [welcomeName, setWelcomeName] = useState(null);

  // Path (a): live event listener — handles the same-session, post-wizard flow
  useEffect(() => {
    const handler = (e) => {
      const detailUid = e?.detail?.u_id;
      const detailName = e?.detail?.name;
      // Guard against cross-user firing if multiple sessions exist somehow
      if (currentUser?.u_id && detailUid && Number(detailUid) !== Number(currentUser.u_id)) return;
      if (detailName) {
        setWelcomeName(detailName);
        try {
          if (detailUid) localStorage.removeItem(`eh_show_welcome_toast_u${detailUid}`);
        } catch { /* ignore */ }
      }
    };
    window.addEventListener('eh:show-welcome-toast', handler);
    return () => window.removeEventListener('eh:show-welcome-toast', handler);
  }, [currentUser?.u_id]);

  // Path (b): localStorage mount-effect — handles the page-reloaded-between flow
  useEffect(() => {
    if (!currentUser?.u_id) return;
    try {
      const key = `eh_show_welcome_toast_u${currentUser.u_id}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        setWelcomeName(stored);
        localStorage.removeItem(key);
      }
    } catch { /* localStorage disabled — silently skip */ }
  }, [currentUser?.u_id]);

  // Chat Center (matrix) store bindings
  const isOpen = useMatrixStore(s => s.isOpen);
  const open   = useMatrixStore(s => s.open);
  const close  = useMatrixStore(s => s.close);
  const setSelectedRoomId = useMatrixStore(s => s.setSelectedRoomId);
  const setSkipAutoSelect = useMatrixStore(s => s.setSkipAutoSelect);

  // Messaging store: used ONLY to clear selection when opening from DockingStation
  const setSelectedThread = useMessagingStore(s => s.setSelectedThread);

  // Design Center store bindings
  const designCenterOpen = useModalStore(s => s.designCenterOpen);
  const openDesignCenter = useModalStore(s => s.openDesignCenter);
  const closeDesignCenter = useModalStore(s => s.closeDesignCenter);
  const bringToFront = useModalStore(s => s.bringToFront);

  // Sharing Center store bindings
  const sharingCenterOpen = useModalStore(s => s.sharingCenterOpen);
  const openSharingCenter = useModalStore(s => s.openSharingCenter);
  const closeSharingCenter = useModalStore(s => s.closeSharingCenter);

  // Help Center store bindings
  const helpCenterOpen = useModalStore(s => s.helpCenterOpen);
  const openHelpCenter = useModalStore(s => s.openHelpCenter);
  const closeHelpCenter = useModalStore(s => s.closeHelpCenter);

  // Walkthrough state
  const activeWalkthrough = useModalStore(s => s.activeWalkthrough);
  const walkthroughStep = useModalStore(s => s.walkthroughStep);
  const nextStep = useModalStore(s => s.nextStep);
  const completeWalkthrough = useModalStore(s => s.completeWalkthrough);
  const profileAssistantZ = useModalStore(s => s.getZIndex('profileAssistant'));

  // Determine which docking station button should pulse gold
  const wtData = activeWalkthrough ? WALKTHROUGHS[activeWalkthrough] : null;
  const currentStepData = wtData ? wtData.steps[walkthroughStep] : null;
  const pulseTarget = currentStepData?.pulseTarget || null;

  // (Optional) if you added a force refresh in matrixStore, wire it here; otherwise remove:
  const forceUpdate = () => {};

  console.log('DockingStation mounting with:', {
    isOwner,
    currentUser,
    u_is_new_user: currentUser?.u_is_new_user,
    authUser: storeProfile
  });

// Auto-open the new-user wizard once on mount IFF the user is mid-onboarding.
// The auto-CLOSE-on-completion branch that used to live here had a fatal bug
// post-lean-onboarding: `onboardingComplete` (u_is_new_user=false + stage=null)
// is the steady state for every signed-in user, so the branch fired on EVERY
// user click — set activeAssistant='profile' → effect re-ran → reset to null.
// Result: the profile-editor icon was visually dead after a fresh signup. The
// auto-close was redundant anyway: ProfileAssistant.handleNameSubmission and
// OngoingProfileAssistant.onClose both call setActiveAssistant(null) directly.
useEffect(() => {
  console.log('DockingStation useEffect triggered:', {
    u_is_new_user: currentUser?.u_is_new_user,
    u_onboarding_stage: currentUser?.u_onboarding_stage,
    activeAssistant
  });

  if (currentUser?.u_is_new_user && currentUser?.u_onboarding_stage !== 'ongoing') {
    console.log('New user detected — starting ProfileAssistant');
    setTimeout(() => {
      setActiveAssistant('profile');
    }, 1000);
  }
}, [currentUser, activeAssistant]);

  // Note: Design Center, Sharing Center, and Messages gold borders are driven
  // directly by their open state from their stores (designCenterOpen, sharingCenterOpen, isOpen).
  // No sync effects needed — multiple modals can show gold borders simultaneously.

  // Ensure docking station is always rendered on the profile page
  if (!currentUser || !isOwner) {
    console.log('DockingStation not rendering due to:', { hasUser: !!currentUser, isOwner });
    return null;
  }
  
  // Ensure docking station remains visible
  return (
    <>
      <style>{GOLD_PULSE_CSS}</style>
      <DockWrapper>
      <DockStation>
        <AssistantButton
          ref={profileButtonRef}
          className={activeAssistant === 'profile' ? 'active' : ''}
          style={pulseTarget === 'profile' ? goldPulseStyle : undefined}
          onClick={() => {
            const wasInactive = activeAssistant !== 'profile';
            setActiveAssistant(activeAssistant === 'profile' ? null : 'profile');
            if (wasInactive && pulseTarget === 'profile') {
              bringToFront('profileAssistant');
              // If this is the last (or only) step, complete the walkthrough; otherwise advance
              const wt = WALKTHROUGHS[activeWalkthrough];
              if (wt && walkthroughStep >= wt.steps.length - 1) {
                completeWalkthrough();
              } else {
                nextStep();
              }
            }
          }}
          title="Profile Assistant"
        >
          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor">
            <path d="M480-240Zm-320 80v-112q0-34 17.5-62.5T224-378q62-31 126-46.5T480-440q37 0 73 4.5t72 14.5l-67 68q-20-3-39-5t-39-2q-56 0-111 13.5T260-306q-9 5-14.5 14t-5.5 20v32h240v80H160Zm400 40v-123l221-220q9-9 20-13t22-4q12 0 23 4.5t20 13.5l37 37q8 9 12.5 20t4.5 22q0 11-4 22.5T903-340L683-120H560Zm300-263-37-37 37 37ZM620-180h38l121-122-18-19-19-18-122 121v38Zm141-141-19-18 37 37-18-19ZM367-527q-47-47-47-113t47-113q47-47 113-47t113 47q47 47 47 113t-47 113q-47 47-113 47t-113-47Zm169.5-56.5Q560-607 560-640t-23.5-56.5Q513-720 480-720t-56.5 23.5Q400-673 400-640t23.5 56.5Q447-560 480-560t56.5-23.5ZM480-640Z"/>
          </svg>
        </AssistantButton>
        <AssistantButton
          className={designCenterOpen ? 'active' : ''}
          style={pulseTarget === 'designCenter' ? goldPulseStyle : undefined}
          onClick={() => {
            if (designCenterOpen) {
              closeDesignCenter();
            } else {
              openDesignCenter();
              bringToFront('designCenter');
              if (pulseTarget === 'designCenter') {
                const wt = WALKTHROUGHS[activeWalkthrough];
                if (wt && walkthroughStep >= wt.steps.length - 1) completeWalkthrough();
                else nextStep();
              }
            }
          }}
          title="Design Center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentcolor"><path d="M240-120q-45 0-89-22t-71-58q26 0 53-20.5t27-59.5q0-50 35-85t85-35q50 0 85 35t35 85q0 66-47 113t-113 47Zm0-80q33 0 56.5-23.5T320-280q0-17-11.5-28.5T280-320q-17 0-28.5 11.5T240-280q0 23-5.5 42T220-202q5 2 10 2h10Zm230-160L360-470l358-358q11-11 27.5-11.5T774-828l54 54q12 12 12 28t-12 28L470-360Zm-190 80Z"/></svg>
        </AssistantButton>
        <AssistantButton
          className={sharingCenterOpen ? 'active' : ''}
          style={pulseTarget === 'sharingCenter' ? goldPulseStyle : undefined}
          onClick={() => {
            if (sharingCenterOpen) {
              closeSharingCenter();
            } else {
              openSharingCenter();
              bringToFront('sharingCenter');
              if (pulseTarget === 'sharingCenter') {
                const wt = WALKTHROUGHS[activeWalkthrough];
                if (wt && walkthroughStep >= wt.steps.length - 1) completeWalkthrough();
                else nextStep();
              }
            }
          }}
          title="Sharing Center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor">
            <path d="M680-80q-50 0-85-35t-35-85q0-6 3-28L282-392q-16 15-37 23.5t-45 8.5q-50 0-85-35t-35-85q0-50 35-85t85-35q24 0 45 8.5t37 23.5l281-164q-2-7-2.5-13.5T560-760q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35q-24 0-45-8.5T598-672L317-508q2 7 2.5 13.5t.5 14.5q0 8-.5 14.5T317-452l281 164q16-15 37-23.5t45-8.5q50 0 85 35t35 85q0 50-35 85t-85 35Zm0-80q17 0 28.5-11.5T720-200q0-17-11.5-28.5T680-240q-17 0-28.5 11.5T640-200q0 17 11.5 28.5T680-160ZM200-440q17 0 28.5-11.5T240-480q0-17-11.5-28.5T200-520q-17 0-28.5 11.5T160-480q0 17 11.5 28.5T200-440Zm508.5-291.5Q720-743 720-760t-11.5-28.5Q697-800 680-800t-28.5 11.5Q640-777 640-760t11.5 28.5Q663-720 680-720t28.5-11.5ZM680-200ZM200-480Zm480-280Z"/>
          </svg>
        </AssistantButton>
        {/* NEW: Chat button (4th button) */}
        <AssistantButton
          className={isOpen ? 'active' : ''}
          style={pulseTarget === 'chat' ? goldPulseStyle : undefined}
          onClick={() => {
            if (isOpen) {
              // Close the Chat Center
              close();
            } else {
              // IMPORTANT:
              // When opening from DockingStation, we want NO conversation selected.
              // Clear both the legacy thread selection and the Matrix room selection
              // so ChatList has no active row and ChatView shows the empty-state UI.
              setSelectedThread(null);
              setSelectedRoomId(null);

              // Tell ChatWorld not to auto-select a conversation
              setSkipAutoSelect(true);

              // Open the Chat Center
              open();
              if (pulseTarget === 'chat') {
                const wt = WALKTHROUGHS[activeWalkthrough];
                if (wt && walkthroughStep >= wt.steps.length - 1) completeWalkthrough();
                else nextStep();
              }
            }
          }}
          title="Chat Center"
        >
          <FaComments size={16} />
        </AssistantButton>


        <AssistantButton
          className={helpCenterOpen ? 'active' : ''}
          style={pulseTarget === 'helpCenter' ? goldPulseStyle : undefined}
          onClick={() => {
            if (helpCenterOpen) {
              closeHelpCenter();
            } else {
              openHelpCenter();
              bringToFront('helpCenter');
              if (pulseTarget === 'helpCenter') {
                const wt = WALKTHROUGHS[activeWalkthrough];
                if (wt && walkthroughStep >= wt.steps.length - 1) completeWalkthrough();
                else nextStep();
              }
            }
          }}
          title="Help Center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor"><path d="M600-120h240v-33q-25-23-56-35t-64-12q-33 0-64 12t-56 35v33Zm162.5-137.5Q780-275 780-300t-17.5-42.5Q745-360 720-360t-42.5 17.5Q660-325 660-300t17.5 42.5Q695-240 720-240t42.5-17.5ZM480-480Zm2-140q-58 0-99 41t-41 99q0 48 27 84t71 50q0-23 .5-44t8.5-38q-14-8-20.5-22t-6.5-30q0-25 17.5-42.5T482-540q15 0 28.5 7.5T533-512q11-5 23-7t24-2h36q-13-43-49.5-71T482-620ZM370-80l-16-128q-13-5-24.5-12T307-235l-119 50L78-375l103-78q-1-7-1-13.5v-27q0-6.5 1-13.5L78-585l110-190 119 50q11-8 23-15t24-12l16-128h220l16 128q13 5 24.5 12t22.5 15l119-50 110 190-85 65H696q-1-5-2-10.5t-3-10.5l86-65-39-68-99 42q-22-23-48.5-38.5T533-694l-13-106h-79l-14 106q-31 8-57.5 23.5T321-633l-99-41-39 68 86 64q-5 15-7 30t-2 32q0 16 2 31t7 30l-86 65 39 68 99-42q24 25 54 42t65 22v184h-70Zm210 40q-25 0-42.5-17.5T520-100v-280q0-25 17.5-42.5T580-440h280q25 0 42.5 17.5T920-380v280q0 25-17.5 42.5T860-40H580Z"/></svg>
        </AssistantButton>
      </DockStation>

      {/* Separate dock card — invite a business you trust. Visually distinct
          from the 5-button stack above by the wrapper's gap. */}
      <DockStation>
        <AssistantButton
          className={inviteOpen ? 'active' : ''}
          onClick={() => setInviteOpen(true)}
          title="Invite a Business You Trust"
        >
          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor">
            <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z"/>
          </svg>
        </AssistantButton>
      </DockStation>
      </DockWrapper>

      <InviteBusinessModal isOpen={inviteOpen} onClose={() => setInviteOpen(false)} />

      {/* Lean-onboarding welcome toast (Slice 2a) — one-shot greeting after
          fresh signup, points at the assistant icon column. Localized to
          the user's preferred language. Auto-dismisses or click X. */}
      {welcomeName && (
        <WelcomeToast
          message={getStrings(currentUser?.u_preferred_language || 'en').welcome_toast.message(welcomeName)}
          onDismiss={() => setWelcomeName(null)}
          targetRef={profileButtonRef}
        />
      )}

        {activeAssistant === 'profile' && (
          <div style={{ zIndex: profileAssistantZ, position: 'relative' }}>
            {currentUser?.u_is_new_user ? (
              <ProfileAssistant
                onClose={() => setActiveAssistant(null)}
                isNewUser={true}
                buttonRef={profileButtonRef}
              />
            ) : (
              <OngoingProfileAssistant
                onClose={() => {
                  console.log("DockingStation onClose triggered");
                  setActiveAssistant(null);
                }}
                buttonRef={profileButtonRef}
              />
            )}
          </div>
        )}



    </>
  );
};

export default DockingStation;